// PENDING DEMO STEPS — ready to add to DemoTourContext.js
// Z-index issue is already resolved (DemoTour.js uses 9999-10001, above wizard z-9000)
//
// TO PREVIEW LOCALLY:
//   1. Open DemoTourContext.js
//   2. Import and spread PENDING_STEPS into TOUR_STEPS at the end
//   3. Run: npm start
//   4. When satisfied, merge permanently and deploy
//
// STEP PLACEMENT IN TOUR:
//   - SETUP_STEPS: insert BEFORE 'workflow-morning' (Act 1 — How to go live)
//   - FEATURE_STEPS: insert AFTER 'clearinghouse-config' (Act 3 — Feature tour additions)

// ── Day in the Life step — insert between SETUP_STEPS and workflow-morning ─────
// NOTE: day-in-life.mp3 needs to be recorded with AWS Polly (Ruth voice) and uploaded
export const DAY_IN_LIFE_STEP = {
  id: 'day-in-life',
  route: '/',
  target: '[data-tour="claim-robot-stats"]',
  title: "A Billing Person's Day in FlowRCM — 20 Minutes",
  description: "Here is what your billing team actually does each day with FlowRCM. They open the dashboard at 8 AM and the AI has already been working for hours. They review the summary — how many claims were scrubbed, how many appeals were filed, how many payments were posted overnight. Then they check the exceptions queue: the two or three claims the AI flagged because it needed a human decision — an unusual denial code, a patient with a complicated history, a claim missing a required attachment. They review those, make the call, and move on. If they have appeal letters to approve before sending, they review those next — takes about five minutes. That is it. Twenty to thirty minutes of exception review and the entire revenue cycle is handled. No portal logins. No manual submissions. No chasing ERA files. No building spreadsheets. The rest of their day is free for patient-facing work, insurance follow-up calls, or whatever the practice actually needs from them. We are about to watch exactly what the AI did before your team logged in this morning.",
  duration: 25000, // Short fallback until day-in-life.mp3 is recorded with AWS Polly
  audioFile: 'day-in-life.mp3',
};

export const SETUP_STEPS = [
  // ── Act 1: Setup — "Here's how you go live in 5 minutes" ─────────────────────
  {
    id: 'setup-welcome',
    route: '/?setup=1',
    target: '[data-tour="onboarding-welcome"]',
    title: 'Setup Takes 5 Minutes — Then It Runs Itself',
    description: "FlowRCM is your automated dental billing platform. Once connected to your practice management system and clearinghouse, it runs your entire revenue cycle on autopilot. Every night it pulls your claims, scrubs them for coding errors, and submits them automatically. When a payer denies a claim, FlowRCM writes the appeal letter and files it before your billing team even sees the denial. When payment comes back, it posts itself. Aging claims get flagged before they hit 90 days. Patient balances get scored and prioritized. Your billing team stops doing the repetitive work and spends their time on exceptions — about 20 minutes a day. Setup takes 5 minutes: connect your practice management system, configure your clearinghouse, and turn on automation. Let's walk through all of it right now.",
    duration: 68000,
    audioFile: 'setup-01.mp3',
  },
  {
    id: 'setup-pms',
    route: '/?setup=1&wizardStep=1',
    target: '[data-tour="wizard-pms-step"]',
    title: 'Step 1 — Connect Your Practice Management System',
    description: "Step one — connect your practice management system. FlowRCM supports WaveOrtho, Open Dental, Eaglesoft, Dentrix Ascend, and Carestream. Select your system and enter your login credentials. For WaveOrtho, Eaglesoft, Dentrix Ascend, and Carestream, enter the username and password your staff uses to log in. For Open Dental, FlowRCM connects directly through the Open Dental REST API — you'll enter your Customer Key in the Integrations tab after setup. FlowRCM syncs your patients, claims, and treatment plans automatically every night. Once configured, you never touch this again.",
    duration: 72000,
    audioFile: 'setup-02.mp3',
  },
  {
    id: 'setup-clearinghouse',
    route: '/?setup=1&wizardStep=2',
    target: '[data-tour="wizard-clearinghouse-step"]',
    title: 'Step 2 — Connect Your Clearinghouse',
    description: "Enter your clearinghouse SFTP credentials — Office Ally, Renaissance, Vyne, or any SFTP-based clearinghouse. This is a one-time connection. Once saved, FlowRCM owns the entire EDI cycle — 837D claim files go out automatically, 835 ERA payment files come back and post themselves, and 278 prior authorization requests push and pull without anyone touching them. Your team stops logging into clearinghouse portals entirely.",
    duration: 68000,
    audioFile: 'setup-03.mp3',
  },
  {
    id: 'setup-automation',
    route: '/?setup=1&wizardStep=3',
    target: '[data-tour="onboarding-automation"]',
    title: 'Step 3 — Turn On Automation',
    description: "Auto appeals, aging alerts, claim scrubbing, payment reminders — all on by default. Every setting can be changed any time. Most practices leave everything on. The ones that do not are typically doing so because they want to manually review certain claim types before the appeal is filed — but even then, FlowRCM flags them automatically. Manual review becomes the exception, not the default.",
    duration: 62000,
    audioFile: 'setup-04.mp3',
  },
  {
    id: 'setup-done',
    route: '/?setup=1&wizardStep=4',
    target: '[data-tour="wizard-done"]',
    title: 'Setup Complete — You\'re Live',
    description: "That is the entire setup. Open Dental connected. Clearinghouse connected. Automation on. From this point forward, claims submit themselves, ERA payments post themselves, denials generate appeal letters automatically, and aging claims get flagged before anyone has to chase them. The practice's billing workflow changes immediately — your team stops doing repetitive tasks and manages only the exceptions that genuinely need a human decision.",
    duration: 65000,
    audioFile: 'setup-05.mp3',
  },
];

export const FEATURE_STEPS = [
  // ── Multi-Location (insert after clearinghouse-config) ────────────────────────
  {
    id: 'multi-location',
    route: '/',
    target: '[data-tour="practice-switcher"]',
    title: '⑮ Multi-Location — Every Practice, One Platform',
    description: "For billing companies and DSOs managing multiple locations, this is where you add each practice. Each location gets its own PMS connection, its own clearinghouse credentials, and its own automation settings — completely independent. Adding a second location takes the same five minutes as the first. Once connected, you switch between practices instantly from the sidebar, and the Claim Robot runs for every location simultaneously. Nine practices running on autopilot, one platform managing all of them.",
    duration: 78000,
    audioFile: 'step-16.mp3',
  },
  // ── Closing step ─────────────────────────────────────────────────────────────
  // NOTE: tour-close.mp3 needs to be recorded with AWS Polly (Ruth voice) and uploaded
  {
    id: 'tour-close',
    route: '/',
    target: '[data-tour="claim-robot-stats"]',
    title: "That's FlowRCM — Fully Automated Dental Billing",
    description: "That's the complete FlowRCM revenue cycle. From claim submission to appeal filing to ERA posting — every step runs on autopilot. Your billing team stops spending their day logging into portals, chasing ERA files, and manually tracking who denied what. They open FlowRCM at eight A.M., review the twenty minutes of exceptions that actually need a human decision, and everything else is already handled. That's what we mean by fully automated dental billing. If you're ready to see what this looks like for your specific practice, book a demo call and we'll walk through your revenue cycle together.",
    duration: 58000,
    audioFile: 'tour-close.mp3',
  },
];

// ── For local preview: add this to DemoTourContext.js ────────────────────────
// import { SETUP_STEPS, FEATURE_STEPS } from './DemoTourSteps-pending';
// Then in TOUR_STEPS:
//   [...SETUP_STEPS, ...existing_workflow_steps, ...existing_feature_steps, ...FEATURE_STEPS]
